import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    TRADE_TYPE,
    TRADE_TYPE_LANG_MAP,
    ORDER_STATUS_LANG_MAP,
    PRICE_TYPE,
    DEPTH,
    ORDER_TYPE,
} from 'src/common/constants';
import { contractNameFormatter, currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledCards } from 'src/components/features/common/card/styledCards';
import { useAppSelector } from 'src/hooks';
import { FutureSymbol, OrderTableContentProps, TradeOrder } from 'src/types';

const OrderHistoryCard: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterOrderHistory, setFilterOrderHistory] = useState([] as Array<TradeOrder>);
    const currentSymbol: FutureSymbol = useAppSelector((state) => state.derivatives.currentSymbol);
    const symbolId = currentSymbol.symbolId;
    const tradeOrders: Array<TradeOrder> = useAppSelector((state) => state.derivatives.tradeOrders);
    const futureSymbols = useAppSelector((state) => state.global.configV2.futuresSymbol);
    useEffect(() => {
        setFilterOrderHistory(
            tradeOrders
                .filter((tradeOrder) => isShowAll || tradeOrder.symbolId === symbolId)
                .sort((a, b) => (a.time - b.time > 0 ? -1 : 1)),
        );
    }, [tradeOrders, symbolId, isShowAll]);

    const columns = [
        {
            title: (
                <FlexContainer styles={{ alignItems: 'center' }}>{t('contracts')}</FlexContainer>
            ),
            key: 'contracts',
            render: (row: { [x: string]: any }) => {
                return (
                    <div
                        className={classNames(
                            'contract',
                            {
                                borderGreen:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                borderRed:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {contractNameFormatter(row['symbolName'])}
                    </div>
                );
            },
        },
        {
            title: t('filled_total'),
            key: 'filledTotal',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {row['executedQty']}/{row['origQty']}
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {row['priceType'] === PRICE_TYPE.MARKET
                            ? t('market_price')
                            : currencyFormatter(
                                  row['price'],
                                  DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                              )}
                    </span>
                );
            },
        },
        {
            title: t('trigger_price'),
            key: 'triggerPrice',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {row['type'] === ORDER_TYPE.CONDITIONAL && row['triggerPrice'] ? (
                            <>
                                {row['triggerPrice'] <= row['price'] ? '<=' : '>='}
                                {currencyFormatter(
                                    row['triggerPrice'],
                                    DEPTH[futureSymbols[row.symbolId]?.minPricePrecision],
                                )}
                                ({t('last')})
                            </>
                        ) : (
                            '--'
                        )}
                    </span>
                );
            },
        },
        {
            title: t('trade_type'),
            key: 'tradeType',
            render: (row: { [x: string]: any }) => {
                const side = row['side'];
                return (
                    <span
                        className={classNames(
                            {
                                greenText:
                                    side === TRADE_TYPE.BUY_OPEN || side === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    side === TRADE_TYPE.SELL_OPEN || side === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {t(TRADE_TYPE_LANG_MAP[side])}
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            render: (row: { [x: string]: any }) => {
                return t(row['priceType'] === PRICE_TYPE.LIMIT ? 'limit' : 'market');
            },
        },
        {
            title: t('status'),
            key: 'status',
            render: (row: { [x: string]: any }) => {
                return <span>{t(ORDER_STATUS_LANG_MAP[row['status']])}</span>;
            },
        },
        {
            title: t('order_no'),
            key: 'orderNo',
            render: (row: { [x: string]: any }) => {
                return <span>{row['orderId']}</span>;
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            render: (row: { [x: string]: any }) => {
                return <span>{DateTimeFormatter(Number(row['time']))}</span>;
            },
        },
    ];
    return <StyledCards columns={columns} data={filterOrderHistory} />;
};

export { OrderHistoryCard };
